import React, { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader } from '@qure/containers'
import miscUtils from 'libs/miscUtils'
import retry from './libs/retryFunction'
import AppLayout from './components/AppLayout'
import BrowserCheck from './components/BrowserCheck'

const AppLogin = lazy(() => retry(() => import('./pages/AppLogin')))
const Dashboard = lazy(() => retry(() => import('./pages/Dashboard')))
const NotFoundPage = lazy(() => retry(() => import('./pages/NotFoundPage')))
const AddEditClient = lazy(() => retry(() => import('./pages/AddEditClient')))
const ClientLists = lazy(() => retry(() => import('./pages/ClientLists')))
const RadiologistLists = lazy(() =>
  retry(() => import('./pages/RadiologistLists'))
)
const ManageUsers = lazy(() => retry(() => import('./pages/ManageUsers')))
const ManageSites = lazy(() => retry(() => import('./pages/ManageSites')))
const Viewer = lazy(() => retry(() => import('./pages/Viewer')))
const Uploads = lazy(() => retry(() => import('./pages/Uploads/ImageUpload')))
const ManageUploads = lazy(() =>
  retry(() => import('./pages/Uploads/ManageUploads'))
)
const DicomUpload = lazy(() =>
  retry(() => import('./pages/Uploads/DicomUpload'))
)
const TestLists = lazy(() => retry(() => import('./pages/TestLists')))
const AppCreateNewPassword = lazy(() =>
  retry(() => import('./pages/AppCreateNewPassword'))
)
const Report = lazy(() => retry(() => import('./pages/Report')))
const ScpStatus = lazy(() => retry(() => import('./pages/ScpStatus')))

const AppRouter = () => {
  const { t } = useTranslation()
  const IS_MWL_ENABLED = miscUtils.getMwlState()

  useEffect(() => {
    const disableScroll = () => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur()
      }
    }

    const disableArrowKeys = e => {
      if (
        document.activeElement.type === 'number' &&
        (e.which === 38 || e.which === 40)
      ) {
        e.preventDefault()
      }
    }

    if (document) {
      document.addEventListener('wheel', disableScroll)
      document.addEventListener('keydown', disableArrowKeys)
    }

    return () => {
      document.removeEventListener('wheel', disableScroll)
      document.removeEventListener('keydown', disableArrowKeys)
    }
  }, [document])

  return (
    <BrowserCheck t={t}>
      <Router>
        <Suspense
          fallback={
            <Loader
              className="page-loader"
              loadingText={{ title: t('Loading the page') }}
            />
          }
        >
          <Switch>
            <AppLayout exact path="/">
              <Dashboard />
            </AppLayout>
            <Route exact path="/login">
              <AppLogin />
            </Route>
            <AppLayout path="/add-client">
              <AddEditClient />
            </AppLayout>
            <AppLayout path="/edit-client/:clientId/:siteName">
              <AddEditClient />
            </AppLayout>
            <AppLayout path="/client-lists">
              <ClientLists />
            </AppLayout>
            {IS_MWL_ENABLED && (
              <AppLayout path="/scp">
                <ScpStatus />
              </AppLayout>
            )}
            <AppLayout path="/radiologist-lists">
              <RadiologistLists />
            </AppLayout>
            <AppLayout path="/tests-lists">
              <TestLists />
            </AppLayout>
            <AppLayout path="/manage-users">
              <ManageUsers />
            </AppLayout>
            <AppLayout path="/manage-sites">
              <ManageSites />
            </AppLayout>
            <AppLayout exact path="/uploads/">
              <ManageUploads />
            </AppLayout>
            <AppLayout path="/uploads/:visitId/:siteId">
              <Uploads />
            </AppLayout>
            <AppLayout path="/dicom/uploads/:visitId?/:siteId?">
              <DicomUpload />
            </AppLayout>
            <AppLayout path="/viewer/:visitId/:siteId">
              <Viewer />
            </AppLayout>
            <Route path="/password-update">
              <AppCreateNewPassword />
            </Route>
            <Route path="/report/:visitId/:siteId">
              <Report />
            </Route>
            <AppLayout path="*">
              <NotFoundPage t={t} />
            </AppLayout>
          </Switch>
        </Suspense>
      </Router>
    </BrowserCheck>
  )
}

AppRouter.defaultProps = {}

AppRouter.propTypes = {}

export default AppRouter
